import React, { useContext } from 'react';
import { ShippingBanner } from '../components/ShippingBanner';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/Button';
import { NavigationContext } from '../App';
import { Heart, Users, Star, Sparkles, Instagram, ArrowRight } from 'lucide-react';
const values = [{
  icon: Heart,
  title: 'Rooted in Heritage',
  description: 'Every piece honors the rich traditions and timeless elegance of African craftsmanship and style.'
}, {
  icon: Users,
  title: 'Community First',
  description: 'Ubuntu—I am because we are. We build connections and celebrate the beauty of African sisterhood.'
}, {
  icon: Star,
  title: 'Unapologetic Excellence',
  description: 'Quality that speaks for itself. Designs that make you feel as powerful as you truly are.'
}];
const milestones = [{
  year: '2019',
  event: 'Started curating pieces from my Lagos apartment'
}, {
  year: '2020',
  event: 'Launched online, serving the African diaspora'
}, {
  year: '2022',
  event: 'Reached 10,000 queens across the globe'
}, {
  year: '2024',
  event: 'Expanded to four heritage-inspired collections'
}];
export function AboutPage() {
  const {
    navigate
  } = useContext(NavigationContext);
  return <div className="min-h-screen flex flex-col bg-[#FDF8F3]" data-id="element-484">
      <ShippingBanner data-id="element-485" />
      <Navbar data-id="element-486" />

      <main className="flex-grow" data-id="element-487">
        {/* Hero Section */}
        <section className="relative overflow-hidden" data-id="element-488">
          {/* African pattern background */}
          <div className="absolute inset-0 african-pattern opacity-20" data-id="element-489" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-490">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 py-12 sm:py-16 lg:py-24 items-center" data-id="element-491">
              <div className="order-2 lg:order-1" data-id="element-492">
                <div className="flex items-center gap-2 mb-4" data-id="element-493">
                  <Sparkles size={16} className="text-[#D4A853]" data-id="element-494" />
                  <span className="text-sm sm:text-base uppercase tracking-[0.2em] text-[#D4A853] font-['Raleway']" data-id="element-495">
                    Our Story
                  </span>
                </div>
                <h1 className="text-4xl sm:text-5xl md:text-6xl font-['Playfair_Display'] text-[#5C3D2E] leading-[1.15] mb-6" data-id="element-496">
                  Hello, I'm{' '}
                  <span className="italic text-[#D4A853]" data-id="element-497">Christiana</span>
                </h1>
                <div className="space-y-4 text-lg sm:text-xl text-[#6B5B4F] font-['Raleway'] leading-relaxed" data-id="element-498">
                  <p data-id="element-499">
                    Growing up in Nigeria, I was surrounded by the vibrant
                    colors, bold patterns, and undeniable elegance of African
                    fashion. That love for our heritage never left me.
                  </p>
                  <p data-id="element-500">
                    I started Christy Fashion Collection in 2019 with a simple
                    mission: to bring the beauty and sophistication of
                    African-inspired fashion to women everywhere. Every piece I
                    curate carries a piece of home—the warmth, the pride, the
                    unapologetic beauty of being African.
                  </p>
                  <p data-id="element-501">
                    Today, I'm honored to dress thousands of queens across the
                    diaspora. This isn't just fashion—it's a celebration of who
                    we are and where we come from.
                  </p>
                </div>
                <div className="mt-8 pt-6 border-t border-[#D4A853]/30" data-id="element-502">
                  <p className="font-['Playfair_Display'] text-3xl sm:text-4xl italic text-[#5C3D2E]" data-id="element-503">
                    Christiana
                  </p>
                  <p className="text-sm sm:text-base text-[#6B5B4F] font-['Raleway'] mt-1 uppercase tracking-wider" data-id="element-504">
                    Founder & Creative Director · Lagos → USA
                  </p>
                </div>
              </div>
              <div className="order-1 lg:order-2" data-id="element-505">
                <div className="relative" data-id="element-506">
                  <div className="aspect-[4/5] bg-[#F5E6D3] relative overflow-hidden" data-id="element-507">
                    {/* Decorative corners inspired by African geometry */}
                    <div className="absolute top-0 right-0 w-20 sm:w-24 h-20 sm:h-24 bg-[#D4A853]/40 z-10" data-id="element-508" />
                    <div className="absolute top-4 right-4 w-16 sm:w-20 h-16 sm:h-20 bg-[#C65D3B]/30 z-10" data-id="element-509" />
                    <div className="absolute bottom-0 left-0 w-16 sm:w-20 h-16 sm:h-20 bg-[#2D5A4A]/20 z-10" data-id="element-510" />
                    <div className="absolute bottom-4 left-4 w-12 sm:w-16 h-12 sm:h-16 bg-[#D4A853]/30 z-10" data-id="element-511" />
                    <img src="https://cdn.magicpatterns.com/uploads/ksWTzFirUa358KYJesShzR/photo_2026-02-04_04-56-59.jpg" alt="Christiana - Founder of Christy Fashion Collection" className="absolute inset-0 w-full h-full object-cover object-top" data-id="element-512" />
                  </div>
                  <div className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6 w-24 sm:w-32 h-24 sm:h-32 bg-[#C65D3B]/20 -z-10" data-id="element-513" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-12 sm:py-16 lg:py-20 bg-white relative" data-id="element-514">
          <div className="absolute top-0 left-0 right-0 h-1 kente-stripe" data-id="element-515" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-516">
            <div className="text-center mb-10 sm:mb-14" data-id="element-517">
              <p className="text-sm sm:text-base uppercase tracking-[0.2em] text-[#6B5B4F] mb-2 sm:mb-3 font-['Raleway']" data-id="element-518">
                What We Believe
              </p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-['Playfair_Display'] text-[#5C3D2E]" data-id="element-519">
                Our Values
              </h2>
              <div className="w-10 sm:w-12 h-[2px] bg-[#D4A853] mx-auto mt-4" data-id="element-520" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12" data-id="element-521">
              {values.map((value, index) => <div key={index} className="text-center" data-id="element-522">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-5 sm:mb-6 rounded-full bg-[#D4A853]/10 flex items-center justify-center border border-[#D4A853]/30" data-id="element-523">
                    <value.icon size={28} className="text-[#D4A853]" strokeWidth={1.5} data-id="element-524" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-['Playfair_Display'] text-[#5C3D2E] mb-2 sm:mb-3" data-id="element-525">
                    {value.title}
                  </h3>
                  <p className="text-lg sm:text-xl text-[#6B5B4F] font-['Raleway'] leading-relaxed max-w-xs mx-auto" data-id="element-526">
                    {value.description}
                  </p>
                </div>)}
            </div>
          </div>
        </section>

        {/* Journey Timeline */}
        <section className="py-12 sm:py-16 lg:py-20 bg-[#FDF8F3]" data-id="element-527">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-528">
            <div className="text-center mb-10 sm:mb-14" data-id="element-529">
              <p className="text-sm sm:text-base uppercase tracking-[0.2em] text-[#6B5B4F] mb-2 sm:mb-3 font-['Raleway']" data-id="element-530">
                The Journey
              </p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-['Playfair_Display'] text-[#5C3D2E]" data-id="element-531">
                Our Milestones
              </h2>
              <div className="w-10 sm:w-12 h-[2px] bg-[#D4A853] mx-auto mt-4" data-id="element-532" />
            </div>
            <div className="relative" data-id="element-533">
              <div className="absolute left-4 sm:left-1/2 top-0 bottom-0 w-px bg-[#D4A853]/30 sm:-translate-x-px" data-id="element-534" />
              <div className="space-y-8 sm:space-y-12" data-id="element-535">
                {milestones.map((milestone, index) => <div key={index} className={`relative flex items-center gap-6 sm:gap-0 ${index % 2 === 0 ? 'sm:flex-row' : 'sm:flex-row-reverse'}`} data-id="element-536">
                    <div className="absolute left-4 sm:left-1/2 w-3 h-3 bg-[#D4A853] rounded-full -translate-x-1/2 z-10 ring-4 ring-[#FDF8F3]" data-id="element-537" />
                    <div className={`ml-10 sm:ml-0 sm:w-1/2 ${index % 2 === 0 ? 'sm:pr-12 sm:text-right' : 'sm:pl-12'}`} data-id="element-538">
                      <span className="text-[#D4A853] font-['Playfair_Display'] text-2xl sm:text-3xl font-medium" data-id="element-539">
                        {milestone.year}
                      </span>
                      <p className="text-lg sm:text-xl text-[#6B5B4F] font-['Raleway'] mt-1" data-id="element-540">
                        {milestone.event}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </section>

        {/* Philosophy Quote */}
        <section className="py-12 sm:py-16 lg:py-20 bg-[#5C3D2E] text-white relative" data-id="element-541">
          <div className="absolute top-0 left-0 right-0 h-1 kente-stripe" data-id="element-542" />
          <div className="absolute inset-0 african-pattern opacity-10" data-id="element-543" />
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-544">
            <div className="w-12 h-[1px] bg-[#D4A853] mx-auto mb-6 sm:mb-8" data-id="element-545" />
            <blockquote className="text-2xl sm:text-3xl md:text-4xl font-['Playfair_Display'] leading-relaxed mb-6 sm:mb-8 italic" data-id="element-546">
              "When an African woman wears her crown, the whole world takes
              notice. My mission is to help every queen find hers."
            </blockquote>
            <p className="text-lg sm:text-xl text-white/70 font-['Raleway']" data-id="element-547">
              — Christiana Nnanna
            </p>
            <div className="w-12 h-[1px] bg-[#D4A853] mx-auto mt-6 sm:mt-8" data-id="element-548" />
          </div>
        </section>

        {/* Connect Section */}
        <section className="py-12 sm:py-16 lg:py-20 bg-[#FDF8F3]" data-id="element-549">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-550">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-['Playfair_Display'] text-[#5C3D2E] mb-4" data-id="element-551">
              Join the Movement
            </h2>
            <p className="text-lg sm:text-xl text-[#6B5B4F] font-['Raleway'] leading-relaxed mb-8 max-w-lg mx-auto" data-id="element-552">
              Follow along for daily style inspiration, behind-the-scenes
              moments, and first access to new arrivals. Welcome to the family,
              sis.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6" data-id="element-553">
              <a href="https://instagram.com/christiana.nnanna" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-[#D4A853]/30 text-[#5C3D2E] hover:border-[#D4A853] hover:text-[#D4A853] transition-colors font-['Raleway'] text-base" data-id="element-554">
                <Instagram size={20} strokeWidth={1.5} data-id="element-555" />
                @christiana.nnanna
              </a>
              <Button onClick={() => navigate('shop')} variant="secondary" className="group bg-[#D4A853] text-[#1A1A1A] hover:bg-[#E8C97A] text-base" data-id="element-556">
                <span data-id="element-557">Shop the Collection</span>
                <ArrowRight size={16} className="ml-2 transform group-hover:translate-x-1 transition-transform" data-id="element-558" />
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-559" />
    </div>;
}